import {
    ConversationStartRequest,
    ConversationParticipantView,
    SendMessageRequest,
    MessagingResource,
    ConversationViewResource,
} from '@mark43/cad-api';
import { useApiClient } from '@m43/core';
import { useCallback } from 'react';
import { ConversationKey } from '../../models';

export const useMessagingResource = () => {
    const apiClient = useApiClient();

    const getBookmarkedConversations = useCallback(async () => {
        const response =
            await apiClient.request<ConversationViewResource.GetBookmarkedConversations>({
                method: 'POST',
                url: 'conversation_view/bookmarked_conversations',
                data: {
                    restrictToLastDay: false,
                    participantFilterIds: [],
                },
            });

        const bookmarkedConversations = response.data.data;

        if (!bookmarkedConversations) {
            throw Error('The bookmarked conversations are missing.');
        }

        return bookmarkedConversations;
    }, [apiClient]);

    const getConversationsBootstrapData = useCallback(
        async (lastReceivedMessageNudgeId?: number) => {
            const response =
                await apiClient.request<ConversationViewResource.GetConversationBootstrapData>({
                    method: 'POST',
                    url: 'conversation_view/bootstrap',
                    data: {
                        excludeHistoricalConversationIds: true,
                        restrictToLastDay: false,
                        lastReceivedMessageNudgeId,
                    },
                });

            const conversationBootstrapResponse = response.data.data;

            if (!conversationBootstrapResponse) {
                throw Error('The conversation bootstrap data is missing.');
            }

            return conversationBootstrapResponse;
        },
        [apiClient]
    );

    const getConversations = useCallback(
        async (conversationId?: number) => {
            const response = await apiClient.request<ConversationViewResource.GetConversations>({
                method: 'POST',
                url: 'conversation_view/conversations',
                data: {
                    restrictToLastDay: false,
                    participantFilterIds: [],
                },
                params: { conversation_id: conversationId },
            });

            const conversations = response.data.data;

            if (!conversations) {
                throw Error('The conversations are missing.');
            }

            return conversations;
        },
        [apiClient]
    );

    const getConversation = useCallback(
        async (conversationId: number) => {
            const conversations = await getConversations(conversationId);
            return conversations.length > 0 ? conversations[0] : null;
        },
        [getConversations]
    );

    const getMessagesForConversation = useCallback(
        async ({
            conversationKey,
            lastReceivedMessageId,
            bookmarksOnly = false,
        }: {
            conversationKey?: ConversationKey;
            lastReceivedMessageId?: number;
            bookmarksOnly?: boolean;
        }) => {
            if (
                conversationKey === undefined ||
                conversationKey.participantIdentity === undefined
            ) {
                throw Error('The conversation participant identity is undefined.');
            }

            const response =
                await apiClient.request<ConversationViewResource.GetMessagesForConversation>({
                    method: 'POST',

                    url: `conversation_view/conversations/${conversationKey.conversationId}/get_messages`,
                    data: {
                        bookmarksOnly,
                        // @ts-expect-error last message received should not be required, api types are incorrect
                        lastReceivedMessageId,
                        participantFilterId: conversationKey.participantIdentity.id,
                        restrictToLastDay: false,
                    },
                });

            const messageList = response.data.data;

            if (!messageList) {
                throw Error('The conversation messages are missing.');
            }

            return messageList;
        },
        [apiClient]
    );

    const putUserMessageBookmarked = useCallback(
        async (messageId: number, isBookmarked: boolean) => {
            const response = await apiClient.request<MessagingResource.PutUserMessageBookmarked>({
                method: 'PUT',
                url: 'messaging/bookmark',
                params: {
                    bookmarked: isBookmarked,
                },
                data: [messageId],
            });

            const success = response.data.success;

            if (!success) {
                throw Error(`Failed to update bookmark for messageId ${messageId}.`);
            }

            return success;
        },
        [apiClient]
    );

    const getRecipientSearchItems = useCallback(
        async (search?: string) => {
            const response =
                await apiClient.request<ConversationViewResource.GetConversationRecipientSearchItemsView>(
                    {
                        method: 'GET',
                        url: `conversation_view/recipients_list`,
                        params: {
                            search,
                        },
                    }
                );

            if (!response.data.success) {
                throw Error('Failed to get recipient search items.');
            }

            return response.data.data;
        },
        [apiClient]
    );

    const getMessageWithParticipantLabelPrefix = (message: SendMessageRequest) => ({
        isUrgent: message.isUrgent,
        senderParticipant: message.senderParticipant,
        text: message.text,
    });

    const createConversation = useCallback(
        async ({ message, members }: ConversationStartRequest) => {
            const response = await apiClient.request<ConversationViewResource.CreateConversation>({
                method: 'POST',
                url: `conversation_view/conversations/create`,
                data: {
                    members,
                    message: message ? getMessageWithParticipantLabelPrefix(message) : undefined,
                },
            });

            if (!response.data.success) {
                throw Error('Failed to start conversation.');
            }

            return response.data.data;
        },
        [apiClient]
    );

    const markConversationAsRead = useCallback(
        async (conversationKey: ConversationKey) => {
            const response =
                await apiClient.request<ConversationViewResource.MarkConversationAsRead>({
                    method: 'PUT',
                    url: `conversation_view/conversations/${conversationKey.conversationId}/read`,
                });

            const success = response.data.success;

            if (!success) {
                throw Error('Failed to mark conversation as read.');
            }

            return success;
        },
        [apiClient]
    );

    const sendMessage = useCallback(
        async ({
            conversationKey,
            isUrgent,
            senderParticipant,
            text,
        }: {
            conversationKey: ConversationKey;
            isUrgent: boolean;
            senderParticipant: ConversationParticipantView;
            text: string;
        }) => {
            const response = await apiClient.request<ConversationViewResource.SendMessage>({
                method: 'POST',
                url: `conversation_view/conversations/${conversationKey.conversationId}/messages`,
                data: getMessageWithParticipantLabelPrefix({ isUrgent, senderParticipant, text }),
            });

            const conversationMessageView = response.data.data;

            if (!conversationMessageView) {
                throw Error(
                    `Failed to send message for conversationId ${conversationKey.conversationId}.`
                );
            }

            return conversationMessageView;
        },
        [apiClient]
    );

    return {
        createConversation,
        getBookmarkedConversations,
        getConversation,
        getConversations,
        getConversationsBootstrapData,
        getMessagesForConversation,
        getRecipientSearchItems,
        markConversationAsRead,
        putUserMessageBookmarked,
        sendMessage,
    };
};
